import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Grid } from '@mui/material';
import { Edit as EditIcon, Search as SearchIcon } from '@mui/icons-material';

import {
    Fetch,
    Header,
    List,
} from '../CommonComponents';
import { Container, Paper } from '../StyledComponents';

import { getWatchlist } from '../../api/watchlists';

import type { FetchComponentPropsType } from '../CommonComponents/Fetch/Fetch';
import { TenantRoute, WatchlistType } from '../../types';
import { useDispatch, useSelector } from '../../redux/hooks';

import {
    selectAllOpenJobs,
    fetchAllOpenJobs,
    fetchAllOpenCapacities,
    setMarketFiltersFromWatchlist,
    selectAllOpenCapacities
} from '../../redux/actions/market';
import EditableCircleMap from '../CommonComponents/GoogleMap/EditableCircleMap';
import EditableRouteMap from '../CommonComponents/GoogleMap/EditableRouteMap';
import useTenantRoutes from '../Market/useTenantRoutes';
import { filterMarketEntities, watchlistToMarketFilters } from '../../utils/filter-market-entities';
import { formatRange } from '../../utils/parsing';

import {
    formatArea,
    formatMatchMode,
    formatNotificationCount,
    formatType,
    getNotificationMode,
    getWatchlistTags
} from './formatWatchlist';
import ListRow from './parts/ListRow';
import NotificationModeControl from './parts/NotificationModeControl';
import NotificationWarning from './parts/NotificationWarning';
import SectionHeader from './parts/SectionHeader';
import TagChip from './parts/TagChip';
import useWatchlistNotificationMode from './parts/useWatchlistNotificationMode';

function WatchlistAreaMap({ watchlist }: { watchlist: WatchlistType }) {
    const routes = useTenantRoutes(!!watchlist.routeId);

    const { area } = watchlist;
    if (area?.type === 'circle') {
        return (
            <EditableCircleMap
                readOnly
                center={area.shape.center}
                radiusM={area.shape.radius}
                height="100%"
            />
        );
    }

    let mapRoutes: TenantRoute[] = [];
    if (area?.type === 'polygon') {
        mapRoutes = [{ id: 1, name: 'Område', points: area.shape.points }];
    } else if (watchlist.routeId) {
        mapRoutes = (routes ?? []).filter((r) => r.id === watchlist.routeId);
    }
    return (
        <EditableRouteMap
            readOnly
            routes={mapRoutes}
            fitToId={mapRoutes[0]?.id ?? null}
            height="100%"
        />
    );
}

function WatchlistDetailsView(props: FetchComponentPropsType<WatchlistType>) {
    const { entity, refetch, isFetching } = props;

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const allOpenJobs = useSelector(selectAllOpenJobs());
    const allOpenCapacities = useSelector(selectAllOpenCapacities());
    const [publishedCount, setPublishedCount] = React.useState<number | null>(null);

    React.useEffect(() => {
        // if (allOpenJobs.length === 0) {
        //     Sätt en flagga här också när det är kört
        // }
        setPublishedCount(filterMarketEntities({
            entities: [...allOpenJobs, ...allOpenCapacities],
            filters: watchlistToMarketFilters(entity)
        }).length);
    }, [allOpenCapacities, allOpenJobs, entity]);

    const { select: selectMode, isSaving: isSavingMode } = useWatchlistNotificationMode(entity, refetch);

    const showResults = React.useCallback(() => {
        dispatch(setMarketFiltersFromWatchlist(entity));
        navigate('/market');
    }, [dispatch, entity, navigate]);

    return (
        <Container>
            <Header
                title={entity.name || 'Namnlös bevakning'}
                subtitle={formatNotificationCount(entity.notificationCount30d || 0)}
                CustomComponent={(
                    <>
                        <Button variant="outlined" startIcon={<SearchIcon />} onClick={showResults}>
                            {`Visa ${publishedCount} träff${publishedCount === 1 ? '' : 'ar'}`}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={() => navigate(`/watchlists/edit/${entity.id}`)}
                        >
                            Redigera
                        </Button>
                    </>
                )}
            />
            <NotificationWarning />
            <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={12} lg={6}>
                    <Paper padding={0} sx={{ overflow: 'hidden' }}>
                        <SectionHeader>Notiser</SectionHeader>
                        <ListRow
                            title="Hur vill du bli notifierad?"
                            tags={getWatchlistTags(entity).map((tag) => (
                                <TagChip key={tag} label={tag} outlined={tag === 'Pausad'} />
                            ))}
                            action={(
                                <NotificationModeControl
                                    value={getNotificationMode(entity)}
                                    onSelect={selectMode}
                                    disabled={isSavingMode}
                                    label="Notiser för bevakningen"
                                />
                            )}
                        />
                        <SectionHeader>Detaljer</SectionHeader>
                        <List
                            data={[{
                                name: 'Typ',
                                value: formatType(entity)
                            }, {
                                name: 'Externa uppdrag',
                                value: entity.isJob && !entity.takesOffer,
                                type: 'bool'
                            }, {
                                name: 'Område',
                                value: formatArea(entity)
                            }, {
                                name: 'Matchning',
                                value: formatMatchMode(entity)
                            }, {
                                name: 'Avsändare',
                                value: entity.publisherTenantName
                            }, {
                                name: 'Totalvikt [kg]',
                                value: formatRange(entity.minGrossWeight, entity.maxGrossWeight, 'kg')
                            }, {
                                name: 'FDR-vikt [kg]',
                                value: formatRange(entity.minChargeableWeight, entity.maxChargeableWeight, 'kg')
                            }, {
                                name: 'Volym [m³]',
                                value: formatRange(entity.minVolume, entity.maxVolume, 'm³')
                            }, {
                                name: 'Flakmeter',
                                value: formatRange(entity.minLoadingMeters, entity.maxLoadingMeters, 'flm')
                            }, {
                                name: 'Pallplatser',
                                value: formatRange(entity.minPalletPlaces, entity.maxPalletPlaces, 'st')
                            }, {
                                name: 'Pallar',
                                value: formatRange(entity.minPallets, entity.maxPallets, 'st')
                            // }, {
                            //     name: 'Stycken',
                            //     value: formatRange(entity.minPieces, entity.maxPieces, 'st')
                            }, {
                                name: 'Endast i grupp',
                                value: entity.groupName
                            }, {
                                name: 'Endast uppdrag med riktpris',
                                value: entity.hasListPrice,
                                type: 'bool'
                            }, {
                                name: 'Skapad',
                                value: entity.createdAt,
                                type: 'datetime'
                            }, {
                                name: 'Uppdaterad',
                                value: entity.updatedAt,
                                type: 'datetime'
                            }]}
                        />
                    </Paper>
                </Grid>
                <Grid item xs={12} lg={6}>
                    <Paper padding={0} sx={{ height: '100%' }}>
                        <WatchlistAreaMap watchlist={entity} />
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
}

export default function WatchlistDetails() {
    const { id } = useParams() as unknown as { id: number };
    const dispatch = useDispatch();

    const fetchWatchlist = React.useCallback(() => {
        dispatch(fetchAllOpenJobs(true)); // Only fetch if not recently fetched
        dispatch(fetchAllOpenCapacities(true)); // Only fetch if not recently fetched

        return getWatchlist(id);
    }, [dispatch, id]);

    return <Fetch<WatchlistType> fetch={fetchWatchlist} Component={WatchlistDetailsView} name="bevakningen" />;
}
